"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeDetail } from '@open-mercato/shared/lib/frontend/useOrganizationScope'

export default function CreateAdapterPage() {
  const t = useT()
  const router = useRouter()
  const { organizationId, tenantId } = useOrganizationScopeDetail()

  const fields = React.useMemo<CrudField[]>(() => [
    {
      id: 'name',
      label: t('rail_tracking.adapters.fields.name', 'Name'),
      type: 'text',
      required: true,
      placeholder: 'Locomotive GPS Tracker #1',
    },
    {
      id: 'adapterType',
      label: t('rail_tracking.adapters.fields.adapterType', 'Adapter Type'),
      type: 'select',
      required: true,
      options: [
        { value: 'GBOX', label: 'G-Box' },
        { value: 'TRIMBLE', label: 'Trimble' },
        { value: 'MQTT_GENERIC', label: 'MQTT Generic' },
        { value: 'WEBHOOK_GENERIC', label: 'Webhook Generic' },
        { value: 'MANUAL', label: 'Manual' },
      ],
    },
    {
      id: 'pollingIntervalSeconds',
      label: t('rail_tracking.adapters.fields.pollingInterval', 'Polling Interval (seconds)'),
      type: 'number',
      defaultValue: 60,
      placeholder: '60',
    },
    {
      id: 'isActive',
      label: t('rail_tracking.adapters.fields.isActive', 'Active'),
      type: 'checkbox',
      defaultValue: true,
    },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'general',
      title: t('rail_tracking.adapters.groups.general', 'General'),
      fields: ['name', 'adapterType'],
    },
    {
      id: 'configuration',
      title: t('rail_tracking.adapters.groups.configuration', 'Configuration'),
      fields: ['pollingIntervalSeconds', 'isActive'],
    },
  ], [t])

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={t('rail_tracking.adapters.create.title', 'New GPS Adapter')}
          backHref="/backend/rail-tracking"
          fields={fields}
          groups={groups}
          submitLabel={t('rail_tracking.adapters.create.submit', 'Create Adapter')}
          cancelHref="/backend/rail-tracking"
          onSubmit={async (values) => {
            const payload = {
              ...values,
              pollingIntervalSeconds: values.pollingIntervalSeconds ? Number(values.pollingIntervalSeconds) : 60,
              organizationId,
              tenantId,
            }
            await createCrud<{ id?: string }>('rail_tracking/adapters', payload)
            flash(t('rail_tracking.adapters.flash.created', 'GPS adapter created'), 'success')
            router.push('/backend/rail-tracking')
          }}
        />
      </PageBody>
    </Page>
  )
}
